import { useEffect, useRef, useState } from 'react';
import DropDownOptions, { type DropDownOption } from '../DropDown/DropDownOptions';
import { type AriaDropDownProps } from '../DropDown/common';
import DropDownSelector from '../DropDown/DropDownSelector';

function createControlElementId(): string {
  return `dropdown-${Math.random().toString(36).slice(2)}-${Math.random().toString(36).slice(2)}`;
}

interface Props {
  options: DropDownOption[];
  optionsClassName: string;
  selector: React.ReactNode;
  selectorId: string;
  selectedId: string;
  ariaLabel: string;
  disabled?: boolean;
}

function DropDown(props: Props): React.ReactNode {
  const { selector, optionsClassName, selectorId, selectedId, disabled, ariaLabel } = props;

  const [pickerVisible, setPickerVisible] = useState(false);
  const [controlId] = useState(createControlElementId);
  const [focusElementId, setFocusElementId] = useState<string | undefined>(undefined);
  const containerRef = useRef<HTMLDivElement>(null);

  const hidePicker = () => {
    setPickerVisible(false);
    setFocusElementId(undefined);
  };

  const onClickSelector = () => {
    if (disabled) return;
    setPickerVisible(!pickerVisible);
  };

  useEffect(() => {
    if (!pickerVisible) return;

    const handleClickOutside = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        hidePicker();
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [pickerVisible]);

  const onSelectValue = (cb: (e: React.UIEvent) => void) => (e: React.UIEvent) => {
    hidePicker();
    cb(e);
  };

  const dropDownAria: AriaDropDownProps = {
    activeDescendantId: selectedId,
    // Only expose to ARIA while the listbox is open — otherwise the referenced
    // option element is not in the DOM (DropDownOptions returns null), making
    // the IDREF invalid.
    activeDescendantElementId: pickerVisible ? selectedId : undefined,
    controlsId: controlId,
    expanded: pickerVisible,
  };

  const options = props.options.map((option) => ({
    ...option,
    onClick: onSelectValue(option.onClick),
  }));

  const onOpenViaKey = () => {
    setPickerVisible(!pickerVisible);
    setFocusElementId(!pickerVisible ? selectedId : undefined);
  };

  return (
    <div ref={containerRef}>
      <DropDownSelector
        id={selectorId}
        className={`${pickerVisible && 'show'} ${disabled && 'disabled'}`}
        onClick={onClickSelector}
        dropDownAria={dropDownAria}
        ariaLabel={ariaLabel}
        onOpenViaKey={onOpenViaKey}
      >
        {selector}
      </DropDownSelector>
      <DropDownOptions
        className={optionsClassName}
        dropDownAria={dropDownAria}
        options={options}
        focusElementId={focusElementId}
        onExit={hidePicker}
      />
    </div>
  );
}

export default DropDown;
